import { createSelector } from "reselect";

import {
  getGraphData,
  getGraphDates,
  getGraphEntries1,
  getGraphParams,
  getTitle,
} from "../redux/reducersCommon/graphAndTableHelpers";
import {
  getSelectedInterval,
  getSelectedIntervalCode,
} from "../redux/reducersCommon/selectedInterval";
import {
  getDetailErrorMessages,
  getItems,
  getItemsErrorMsg,
  getMeta,
  getPagination,
  getTableFilters,
  getTableQuery,
  getTableSorting,
  getTalkers,
  getThreats,
  getWindowsEvts,
} from "./common/helpers";
import { orgIdSelector } from "./orgIdSelector";

/*
Constants and helpers
*/

const HOSTNAME_DETAIL_ERROR_KEYS = [
  "graphError",
  "talkersError",
  "threatsError",
  "windowsEvtsError",
];

/*
Selectors
*/

const hostSelector = (state) => state.host;
const selectLocation = (state) => state.location;

/*
Host ID selector
*/

const getHostId = (location) => {
  const { payload = {} } = location;

  const { id1 = "" } = payload;

  return id1;
};

// tableFiltersByOrg selector
const filtersSelector = createSelector(
  [orgIdSelector, hostSelector],
  getTableFilters
);

const getFilterValues = (filters) => {
  const { created = {}, firstSeen = {}, lastSeen = {}, name = {} } = filters;

  const { key: createdOption } = created;
  const { key: firstSeenOption } = firstSeen;
  const { key: lastSeenOption } = lastSeen;
  const { value: nameValue } = name;

  return {
    createdOption,
    firstSeenOption,
    lastSeenOption,
    nameValue,
  };
};

export const filterValuesSelector = createSelector(
  [filtersSelector],
  getFilterValues
);

// tableSorting selector
export const sortingSelector = createSelector(
  [orgIdSelector, hostSelector],
  getTableSorting
);

export const idSelector = createSelector([selectLocation], getHostId);

// Interval selector
export const intervalSelector = createSelector(
  [hostSelector, idSelector],
  getSelectedInterval()
);

// Interval code selector
export const intervalCodeSelector = createSelector(
  [intervalSelector],
  getSelectedIntervalCode
);

// Graph raw data selector
export const graphDataSelector = createSelector(
  [intervalCodeSelector, hostSelector, idSelector],
  getGraphData
);

// Graph prepared data/entries selector
export const graphEntriesSelector = createSelector(
  [graphDataSelector],
  getGraphEntries1({ labelKey1: "name" })
);

// Graph dates selector
export const graphDatesSelector = createSelector(
  [intervalCodeSelector, hostSelector, idSelector],
  getGraphDates
);

// Graph params selector
export const graphParamsSelector = createSelector(
  [intervalCodeSelector, hostSelector, idSelector],
  getGraphParams
);

// Hostnames selector
export const hostnamesSelector = createSelector(
  [orgIdSelector, hostSelector],
  getItems
);

// IPs error message selector
export const hostsErrorMsgSelector = createSelector(
  [hostSelector],
  getItemsErrorMsg
);

// metaByOrg selector
export const metaSelector = createSelector(
  [orgIdSelector, hostSelector],
  getMeta
);

// paginationByOrg selector
export const paginationSelector = createSelector(
  [orgIdSelector, hostSelector],
  getPagination
);

// Selects all data required for fetching table data
export const tableQuerySelector = createSelector(
  [
    filtersSelector,
    orgIdSelector,
    paginationSelector,
    sortingSelector,
    () => null, // intervalSelector
  ],
  getTableQuery
);

// Talkers selector
export const talkersSelector = createSelector(
  [intervalCodeSelector, hostSelector, idSelector],
  getTalkers
);

// Host Detail page title
export const titleSelector = createSelector(
  [graphDataSelector],
  getTitle("name")
);

// Threats selector
export const threatsSelector = createSelector(
  [intervalCodeSelector, hostSelector, idSelector],
  getThreats
);

// Windows events selector
export const windowsEvtsSelector = createSelector(
  [intervalCodeSelector, hostSelector, idSelector],
  getWindowsEvts
);

// Error messages selector
export const detailErrorMsgSelector = createSelector(
  [hostSelector],
  getDetailErrorMessages(HOSTNAME_DETAIL_ERROR_KEYS)
);
